import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { logo } from '@clofo/ui/images'
import { Button } from '@clofo/ui/button'
import { usePageTitle } from '@clofo/ui/hooks'
import { useTranslation } from '@clofo/i18n'

import { RequestEmailForm } from '../components/request-email-form'
import { RequestPasswordForm } from '../components/request-password-form'

/**
 * Adaptive login. Step one collects the email and probes for SSO: an SSO-enabled domain redirects
 * to the identity provider, otherwise we advance to the password step. Modeled on the legacy login
 * app's email-first flow.
 */
export const LoginPage = () => {
  const { t } = useTranslation()
  usePageTitle(t('login.pageTitle'))

  // When returning from the forgot-password page (its Cancel action) the prior email is carried in
  // navigation state, so we resume directly on the password step instead of the email step.
  const { state } = useLocation()
  const [email, setEmail] = useState<string | null>(
    (state as { email?: string } | null)?.email || null,
  )

  return (
    <main className='flex flex-col bg-background items-center justify-center'>
      <h1 className='sr-only'>{t('login.pageTitle')}</h1>
      <img src={logo} alt='Closing Folders' className='my-13 h-15' />
      {email === null ? (
        <RequestEmailForm onPasswordRequired={setEmail} />
      ) : (
        <RequestPasswordForm email={email} onChangeEmail={() => setEmail(null)} />
      )}
      {import.meta.env.DEV && (
        // Pinned bottom-right, mirroring the scenario dropdown at bottom-left — dev chrome stays
        // at the screen edges, out of the page flow.
        <div className='fixed bottom-5 right-5 z-50'>
          <Button variant='link' asChild className='p-0'>
            {/* Dev-only stand-in for the invitation email — no email exists against the MSW
                mocks, so this jumps straight to the activation form with a placeholder key. */}
            <Link to='/registration?activationKey=abc123def456'>Open invite link (dev only)</Link>
          </Button>
        </div>
      )}
    </main>
  )
}
